
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, User, Bell, Mail, Phone, CreditCard, Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import GiftPreferences from './GiftPreferences';
import { Switch } from '@/components/ui/switch';
import { Form, FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

const UserProfile = () => {
  const { toast } = useToast();
  const [name, setName] = useState('Carl Johnson');
  const [email, setEmail] = useState('carl@example.com');
  const [phone, setPhone] = useState('(555) 123-4567');
  const [isSaving, setIsSaving] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  
  // Notification preferences state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);
  
  // Payment methods state
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [savedCards, setSavedCards] = useState([
    { id: '1', last4: '4242', brand: 'Visa', expMonth: '12', expYear: '2027', isDefault: true },
    { id: '2', last4: '1234', brand: 'Mastercard', expMonth: '09', expYear: '2025', isDefault: false },
  ]);
  
  const handleSaveProfile = () => {
    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      toast({
        title: "Profile Updated",
        description: "Your account settings have been saved successfully.",
      });
    }, 1000);
  };
  
  const handleSaveNotifications = () => {
    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      toast({
        title: "Notifications Updated",
        description: "Your notification preferences have been saved successfully.",
      });
    }, 1000);
  };
  
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setProfileImage(e.target.result as string);
          toast({
            title: "Profile Image Updated",
            description: "Your profile image has been updated successfully.",
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSubmitCard = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      const newCard = {
        id: (savedCards.length + 1).toString(),
        last4: cardNumber.slice(-4),
        brand: cardNumber.startsWith('4') ? 'Visa' : 'Mastercard',
        expMonth: cardExpiry.split('/')[0],
        expYear: '20' + cardExpiry.split('/')[1],
        isDefault: savedCards.length === 0,
      };
      
      setSavedCards([...savedCards, newCard]);
      setCardNumber('');
      setCardName('');
      setCardExpiry('');
      setCardCvc('');
      setIsAddingCard(false);
      setIsSaving(false);
      
      toast({
        title: "Payment Method Added",
        description: "Your new payment method has been added successfully.",
      });
    }, 1000);
  };
  
  const handleRemoveCard = (id: string) => {
    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      const updatedCards = savedCards.filter(card => card.id !== id);
      
      // If we removed the default card and there are other cards, make the first one default
      if (savedCards.find(card => card.id === id)?.isDefault && updatedCards.length > 0) {
        updatedCards[0].isDefault = true;
      }
      
      setSavedCards(updatedCards);
      setIsSaving(false);
      
      toast({
        title: "Payment Method Removed",
        description: "Your payment method has been removed successfully.",
      });
    }, 1000);
  };
  
  const handleSetDefaultCard = (id: string) => {
    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      const updatedCards = savedCards.map(card => ({
        ...card,
        isDefault: card.id === id,
      }));
      
      setSavedCards(updatedCards);
      setIsSaving(false);
      
      toast({
        title: "Default Payment Method Updated",
        description: "Your default payment method has been updated successfully.",
      });
    }, 1000);
  };
  
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const chunks = [];
    
    for (let i = 0; i < cleaned.length; i += 4) {
      chunks.push(cleaned.substr(i, 4));
    }
    
    return chunks.join(' ').substr(0, 19); // max 16 digits + 3 spaces
  };
  
  const formatExpiryDate = (value: string) => {
    const cleaned = value.replace(/[^0-9]/gi, '');
    
    if (cleaned.length <= 2) {
      return cleaned;
    }
    
    return `${cleaned.substr(0, 2)}/${cleaned.substr(2, 2)}`;
  };
  
  return (
    <div className="container mx-auto py-6 max-w-5xl">
      <h1 className="text-2xl font-bold mb-6">My Profile</h1>
      
      <Tabs defaultValue="account" className="space-y-6">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="payment">Payment</TabsTrigger>
        </TabsList>
        
        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Account Settings</CardTitle>
              <CardDescription>
                Manage your account settings and profile information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative">
                    <div className="h-24 w-24 rounded-full overflow-hidden bg-muted flex items-center justify-center border">
                      {profileImage ? (
                        <img 
                          src={profileImage} 
                          alt="Profile" 
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <User className="h-12 w-12 text-muted-foreground" />
                      )}
                    </div>
                    <Label 
                      htmlFor="profile-image" 
                      className="absolute bottom-0 right-0 bg-primary text-primary-foreground rounded-full p-1 cursor-pointer hover:bg-primary/90"
                    >
                      <Check className="h-4 w-4" />
                    </Label>
                    <Input 
                      id="profile-image" 
                      type="file" 
                      accept="image/*" 
                      className="sr-only"
                      onChange={handleImageUpload}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">Click the check button to upload a new profile image</p>
                </div>
                
                <div className="flex-1 space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input 
                      id="name" 
                      value={name} 
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input 
                      id="phone" 
                      value={phone} 
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Your phone number"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full md:w-auto bg-givgo-purple hover:bg-givgo-purple/90"
                onClick={handleSaveProfile}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="preferences">
          <GiftPreferences />
        </TabsContent>
        
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Settings</CardTitle>
              <CardDescription>
                Configure how and when you receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-full">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium">Email Notifications</h4>
                      <p className="text-sm text-muted-foreground">Receive updates about gifts, special days, and friend activity via email</p>
                    </div>
                  </div>
                  <Switch 
                    checked={emailNotifications}
                    onCheckedChange={setEmailNotifications}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-full">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium">SMS Notifications</h4>
                      <p className="text-sm text-muted-foreground">Receive text messages for gift related activities and friend reminders</p>
                    </div>
                  </div>
                  <Switch 
                    checked={smsNotifications}
                    onCheckedChange={setSmsNotifications}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-full">
                      <Bell className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium">Push Notifications</h4>
                      <p className="text-sm text-muted-foreground">Receive push notifications for real-time updates in the app</p>
                    </div>
                  </div>
                  <Switch 
                    checked={pushNotifications}
                    onCheckedChange={setPushNotifications}
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full md:w-auto bg-givgo-purple hover:bg-givgo-purple/90"
                onClick={handleSaveNotifications}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Notification Preferences'}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="payment">
          <Card>
            <CardHeader>
              <CardTitle>Payment Methods</CardTitle>
              <CardDescription>
                Manage your saved payment methods for quick gifting
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Saved Cards */}
              {savedCards.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Saved Payment Methods</h3>
                  <div className="space-y-3">
                    {savedCards.map((card) => (
                      <div 
                        key={card.id} 
                        className={`flex items-center justify-between p-4 border rounded-lg ${card.isDefault ? 'bg-muted/50 border-primary' : ''}`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary/10 rounded-full">
                            <CreditCard className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <p className="font-medium">
                                {card.brand} •••• {card.last4}
                              </p>
                              {card.isDefault && (
                                <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                                  Default
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Expires {card.expMonth}/{card.expYear.substring(2)}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          {!card.isDefault && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleSetDefaultCard(card.id)}
                              disabled={isSaving}
                            >
                              Set Default
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => handleRemoveCard(card.id)}
                            disabled={isSaving}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Add New Card */}
              <Collapsible open={isAddingCard} onOpenChange={setIsAddingCard}>
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium">Add Payment Method</h3>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" size="sm" className="flex items-center gap-1">
                      {isAddingCard ? 'Cancel' : (
                        <>
                          <Plus className="h-4 w-4" />
                          Add New Card
                        </>
                      )}
                    </Button>
                  </CollapsibleTrigger>
                </div>
                
                <CollapsibleContent className="mt-4">
                  <form onSubmit={handleSubmitCard} className="space-y-4 border rounded-lg p-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="grid gap-2">
                        <Label htmlFor="card-number">Card Number</Label>
                        <Input
                          id="card-number"
                          placeholder="1234 5678 9012 3456"
                          value={cardNumber}
                          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                          maxLength={19}
                          required
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="card-name">Cardholder Name</Label>
                        <Input
                          id="card-name"
                          placeholder="John Doe"
                          value={cardName}
                          onChange={(e) => setCardName(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="grid gap-2">
                        <Label htmlFor="card-expiry">Expiry Date</Label>
                        <Input
                          id="card-expiry"
                          placeholder="MM/YY"
                          value={cardExpiry}
                          onChange={(e) => setCardExpiry(formatExpiryDate(e.target.value))}
                          maxLength={5}
                          required
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="card-cvc">CVC</Label>
                        <Input
                          id="card-cvc"
                          placeholder="123"
                          value={cardCvc}
                          onChange={(e) => setCardCvc(e.target.value.replace(/[^0-9]/g, ''))}
                          maxLength={3}
                          required
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full md:w-auto bg-givgo-purple hover:bg-givgo-purple/90"
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save Card'}
                    </Button>
                  </form>
                </CollapsibleContent>
              </Collapsible>
              
              {/* No Cards Message */}
              {savedCards.length === 0 && !isAddingCard && (
                <div className="text-center p-6 border border-dashed rounded-lg">
                  <CreditCard className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <h3 className="text-lg font-medium mb-1">No payment methods yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add a payment method to make gifting faster and easier
                  </p>
                  <Button 
                    variant="outline" 
                    className="mx-auto"
                    onClick={() => setIsAddingCard(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Payment Method
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserProfile;
